import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import { OperatorTypes } from '@avtechsoftware/ra-javascript-utilities/src/types/OperatorTypes';
import BaseFacet from '../../../../../../classes/facets/BaseFacet/BaseFacet';
import { selectTagList } from '../../../../../../redux/slices/tagListSlice/tagListSlice';
import searchFilterPropType from '../../../../../../utility/PropTypes/searchFilterPropType';
import AdvancedSearchUtility from '../../../../../../utility/AdvancedSearchUtility';
import FacetType from '../../../../../../utility/types/facetType';
import { selectQuietTimes } from '../../../../../../redux/slices/quietTimesSlice/quietTimesSlice';
import {
  selectNotificationGroupList,
} from '../../../../../../redux/slices/notificationGroupsSlice/notificationGroupsSlice';
import {
  selectNotificationTemplates,
} from '../../../../../../redux/slices/notificationTemplatesSlice/notificationTemplatesSlice';

const operatorValueMap = {
  anyOf: OperatorTypes.EQUAL_TO_VALUE,
  allOf: OperatorTypes.EQUAL_TO_BOOLEAN,
  noneOf: OperatorTypes.NOT_EQUAL_TO,
};

function FilterModalPatternA({
  facetType,
  facetClass,
  facets,
  addFacet,
  removeFacet,
  clearFacets,
}) {
  const quietTimes = useSelector(selectQuietTimes);
  const tagList = useSelector(selectTagList);
  const notificationGroupList = useSelector(selectNotificationGroupList);
  const templates = useSelector(selectNotificationTemplates);

  let itemList;
  switch (facetType.id) {
    case FacetType.ALERT_QUIET_TIME:
      itemList = quietTimes;
      break;
    case FacetType.NOTIFICATION_GROUP:
      itemList = notificationGroupList;
      break;
    case FacetType.ALERT_NOTIFICATION_TEMPLATES:
      itemList = templates;
      break;
    case FacetType.ALERT_TAGS:
    default:
      itemList = tagList;
      break;
  }

  const options = facetClass.getValueSelectOptions(itemList);

  const [anyOfOptions, setAnyOfOptions] = useState(options);
  const [allOfOptions, setAllOfOptions] = useState(options);
  const [noneOfOptions, setNoneOfOptions] = useState(options);
  const [anyOfSelected, setAnyOfSelected] = useState([]);
  const [allOfSelected, setAllOfSelected] = useState([]);
  const [noneOfSelected, setNoneOfSelected] = useState([]);

  // eslint-disable-next-line max-len
  const anyOfFacets = facets.filter((facet) => Number(facet.operatorId) === OperatorTypes.EQUAL_TO_VALUE);
  // eslint-disable-next-line max-len
  const allOfFacets = facets.filter((facet) => Number(facet.operatorId) === OperatorTypes.EQUAL_TO_BOOLEAN);
  // eslint-disable-next-line max-len
  const noneOfFacets = facets.filter((facet) => Number(facet.operatorId) === OperatorTypes.NOT_EQUAL_TO);

  const handleAddFacetValue = (value, selectName) => {
    const operator = operatorValueMap[selectName];
    const newFacet = {
      facetId: facetType.id,
      operatorId: operator,
      value1: value.toString(),
      value2: '',
    };
    addFacet(newFacet);
  };

  const handleSelectValueOnChange = (values, action) => {
    if (action.action === 'select-option') {
      handleAddFacetValue(
        values.length > 0 ? values[values.length - 1].value : '',
        action.name,
      );
    }
    /* istanbul ignore next */
    if (action.action === 'remove-value') {
      const facetToRemoveIndex = facets
        .findIndex((facet) => facet.value1 === action.removedValue.value);
      removeFacet(facetToRemoveIndex);
    }

    if (action.action === 'clear') {
      clearFacets();
    }
  };

  React.useEffect(() => {
    if (facets.length > 0) {
      const anySelected = AdvancedSearchUtility.getSelectedOptionsByValue(
        options,
        anyOfFacets,
        'value1',
      );
      const allSelected = AdvancedSearchUtility.getSelectedOptionsByValue(
        options,
        allOfFacets,
        'value1',
      );
      const noneSelected = AdvancedSearchUtility.getSelectedOptionsByValue(
        options,
        noneOfFacets,
        'value1',
      );
      setAnyOfSelected(anySelected);
      setAllOfSelected(allSelected);
      setNoneOfSelected(noneSelected);
      // eslint-disable-next-line max-len
      setAnyOfOptions(AdvancedSearchUtility.filterSelectedOptions(options, [...noneSelected, ...allSelected]));
      // eslint-disable-next-line max-len
      setAllOfOptions(AdvancedSearchUtility.filterSelectedOptions(options, [...noneSelected, ...anySelected]));
      // eslint-disable-next-line max-len
      setNoneOfOptions(AdvancedSearchUtility.filterSelectedOptions(options, [...allSelected, ...anySelected]));
    }
  }, []);

  /* istanbul ignore next */
  return (
    <div>
      <h6>
        {`${facetClass.getDisplayName()} ${facetClass.getDescription()}`}
      </h6>

      <form data-testid="form">
        <div className="row my-3">
          <div className="offset-sm-2 col-sm-8">
            <div className="form-group">
              <label htmlFor="anyOf">
                <b>ANY</b>
                {' '}
                of the following:
              </label>
              <Select
                isMulti
                name="anyOf"
                options={anyOfOptions}
                onChange={handleSelectValueOnChange}
                value={anyOfSelected}
              />
            </div>
          </div>
        </div>
        <div className="row my-3">
          <div className="offset-sm-2 col-sm-8">
            <div className="form-group">
              <label htmlFor="allOf">
                <b>ALL</b>
                {' '}
                of the following:
              </label>
              <Select
                isMulti
                inputId="allOf"
                name="allOf"
                options={allOfOptions}
                onChange={handleSelectValueOnChange}
                value={allOfSelected}
              />
            </div>
          </div>
        </div>
        <div className="row my-3">
          <div className="offset-sm-2 col-sm-8">
            <div className="form-group">
              <label htmlFor="noneOf">
                <b>NONE</b>
                {' '}
                of the following:
              </label>
              <Select
                isMulti
                name="noneOf"
                options={noneOfOptions}
                onChange={handleSelectValueOnChange}
                value={noneOfSelected}
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
FilterModalPatternA.propTypes = {
  facetType: PropTypes.shape({
    id: PropTypes.number,
    validOperators: PropTypes.arrayOf(PropTypes.number),
    displayValue: PropTypes.string,
    modalType: PropTypes.number,
  }).isRequired,
  facets: PropTypes.arrayOf(PropTypes.shape(searchFilterPropType)).isRequired,
  addFacet: PropTypes.func.isRequired,
  removeFacet: PropTypes.func.isRequired,
  facetClass: PropTypes.instanceOf(BaseFacet).isRequired,
  clearFacets: PropTypes.func.isRequired,
};
export default FilterModalPatternA;
